import { useCallback, useEffect, useRef, useState } from "react";
import { getAdminAnalytics } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, Users, Eye, BarChart3, Globe, Smartphone, Monitor, Tablet, Clock } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const DeviceIcon = ({ name, className }) => {
  const k = (name || "").toLowerCase();
  if (k.includes("mobile")) return <Smartphone className={className} />;
  if (k.includes("tablet")) return <Tablet className={className} />;
  return <Monitor className={className} />;
};

const timeAgo = (iso) => {
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return "adesso";
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h fa`;
    return `${Math.floor(h / 24)} g fa`;
  } catch (e) {
    return "";
  }
};

const KpiCard = ({ icon: Icon, label, value, accent, live, testid }) => (
  <Card data-testid={testid} className="rounded-2xl border-border p-5 glow">
    <div className="flex items-center justify-between">
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
      {live && (
        <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-500">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          Live
        </span>
      )}
    </div>
    <div className="mt-4 font-display text-3xl font-semibold tracking-tight">{value}</div>
    <div className="mt-1 text-sm text-muted-foreground">{label}</div>
  </Card>
);

export const AdminDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const d = await getAdminAnalytics(token);
      setData(d);
      setUpdatedAt(new Date());
    } catch (e) {
      /* silent: refresh continuera' al prossimo tick */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 15000);
    return () => clearInterval(timer.current);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const hourly = data?.hourly || { labels: [], data: [] };
  const maxBar = Math.max(1, ...(hourly.data.length ? hourly.data : [1]));
  const topPages = data?.top_pages || [];
  const maxPage = Math.max(1, ...(topPages.length ? topPages.map((p) => p.count) : [1]));
  const devices = data?.devices || [];
  const totalDevices = Math.max(1, devices.reduce((a, b) => a + b.count, 0));
  const recent = data?.recent || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Aggiornamento automatico ogni 15s
          {updatedAt ? ` · ultimo: ${updatedAt.toLocaleTimeString("it-IT")}` : ""}
        </p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid={ADMIN.kpiOnline} icon={Activity} label="Online adesso (5 min)" value={data?.online ?? 0} accent="bg-emerald-500/10 text-emerald-500" live />
        <KpiCard testid={ADMIN.kpiToday} icon={Eye} label="Visite oggi" value={data?.today ?? 0} accent="bg-primary/10 text-primary" />
        <KpiCard testid={ADMIN.kpiSessions} icon={Users} label="Sessioni oggi" value={data?.sessions_today ?? 0} accent="bg-accent/10 text-accent" />
        <KpiCard testid={ADMIN.kpiTotal} icon={BarChart3} label="Visite totali" value={data?.total ?? 0} accent="bg-secondary text-secondary-foreground" />
      </div>

      {/* Grafico 24h */}
      <Card className="rounded-2xl border-border p-5">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-accent" />
          <h3 className="font-semibold">Visite nelle ultime 24 ore</h3>
        </div>
        <div className="mt-6 flex h-40 items-end gap-1">
          {hourly.data.map((v, i) => (
            <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1" title={`${hourly.labels[i]}: ${v} visite`}>
              <span className="text-[10px] text-muted-foreground">{v > 0 ? v : ""}</span>
              <div
                className="w-full rounded-t-md bg-accent/80 transition-[height] duration-300"
                style={{ height: `${Math.max(v > 0 ? 6 : 2, (v / maxBar) * 100)}%` }}
              />
              {i % 3 === 0 && <span className="text-[9px] text-muted-foreground">{hourly.labels[i]}</span>}
            </div>
          ))}
        </div>
      </Card>

      {/* Top pagine + Dispositivi */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="rounded-2xl border-border p-5">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-accent" />
            <h3 className="font-semibold">Pagine più visitate (30 giorni)</h3>
          </div>
          <div className="mt-4 space-y-3">
            {topPages.length === 0 && <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p>}
            {topPages.map((p, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate font-mono text-xs">{p.label}</span>
                  <span className="font-medium">{p.count}</span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${(p.count / maxPage) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-2xl border-border p-5">
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-accent" />
            <h3 className="font-semibold">Dispositivi</h3>
          </div>
          <div className="mt-4 space-y-3">
            {devices.length === 0 && <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p>}
            {devices.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                  <DeviceIcon name={d.label} className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>{d.label}</span>
                    <span className="font-medium">{d.count} · {Math.round((d.count / totalDevices) * 100)}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${(d.count / totalDevices) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Visite recenti */}
      <Card className="rounded-2xl border-border overflow-hidden">
        <div className="flex items-center gap-2 p-5 pb-0">
          <Clock className="h-4 w-4 text-accent" />
          <h3 className="font-semibold">Visite recenti</h3>
        </div>
        <Table className="mt-4">
          <TableHeader>
            <TableRow>
              <TableHead>Pagina</TableHead>
              <TableHead>Provenienza</TableHead>
              <TableHead>Dispositivo</TableHead>
              <TableHead className="text-right">Quando</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recent.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{r.path}</TableCell>
                <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">{r.referrer}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="rounded-md gap-1">
                    <DeviceIcon name={r.device} className="h-3 w-3" />
                    {r.device}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">{timeAgo(r.ts)}</TableCell>
              </TableRow>
            ))}
            {recent.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Ancora nessuna visita registrata.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
